import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { getMaxStartDate, getMaxBacktestYears, getTierDisplayName } from '../../utils/tiers';
import {
  fetchBacktestConfigs,
  createBacktestConfig,
  updateBacktestConfig,
  deleteBacktestConfig
} from '../../lib/api/backtestConfigs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { FiTrash2 } from 'react-icons/fi';

const DEFAULT_WEIGHTING = { method: 'equal', weights: {} };
const DEFAULT_REBALANCE = { freq: '6M', intra_period_trading: false };
const DEFAULT_COSTS = { fee_bps: 2, slippage_bps: 5, dividends: 'auto_reinvest' };
const DEFAULT_DATA_HANDLING = { exclude_large_moves: true, show_large_moves: false };
const BENCHMARK_OPTIONS = [
  { value: '', label: 'No Benchmark' },
  { value: '^GSPC', label: '^GSPC (S&P 500)' },
  { value: 'CSP1.L', label: 'CSP1.L (iShares Core S&P 500 UCITS ETF USD (Acc))' },
  { value: 'CNX1.L', label: 'CNX1.L (iShares NASDAQ 100 UCITS ETF USD (Acc))' }
];

function buildDefaultConfig(billingTier = 'free') {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() - 1);
  const startDate = getMaxStartDate(endDate, billingTier);
  return {
    weighting: { ...DEFAULT_WEIGHTING },
    rebalance: { ...DEFAULT_REBALANCE },
    costs: { ...DEFAULT_COSTS },
    data_handling: { ...DEFAULT_DATA_HANDLING },
    backtest: {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      initial_cash: 100000,
      benchmark: '^GSPC'
    }
  };
}

const normalizeConfig = (config, billingTier) => {
  const fallback = buildDefaultConfig(billingTier);
  if (!config) return fallback;
  return {
    weighting: config.weighting || fallback.weighting,
    rebalance: config.rebalance || fallback.rebalance,
    costs: config.costs || fallback.costs,
    data_handling: config.data_handling || fallback.data_handling,
    backtest: config.backtest || config.run || fallback.backtest
  };
};

const BacktestConfigsPage = ({ embedded = false }) => {
  const { billingTier } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    is_default: false,
    config: buildDefaultConfig()
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const activeConfig = useMemo(
    () => configs.find((cfg) => cfg.id === activeId) || null,
    [configs, activeId]
  );
  const isPlanDefault = Boolean(activeConfig?.is_plan_default);
  const excludeLargeMoves = formState.config?.data_handling?.exclude_large_moves ?? true;

  const hydrateForm = useCallback((cfg) => {
    const normalized = normalizeConfig(cfg?.config, billingTier || 'free');
    setFormState({
      name: cfg?.name || '',
      description: cfg?.description || '',
      is_default: Boolean(cfg?.is_default),
      config: normalized
    });
  }, [billingTier]);

  const loadConfigs = useCallback(async (preferredId = null) => {
    try {
      setLoading(true);
      setError('');
      const list = await fetchBacktestConfigs();
      setConfigs(list);
      if (list.length > 0) {
        const nextId = preferredId;
        const next = list.find((cfg) => cfg.id === nextId)
          || list.find((cfg) => cfg.is_default)
          || list[0];
        setActiveId(next.id);
        hydrateForm(next);
      } else {
        setActiveId(null);
        setFormState({
          name: '',
          description: '',
          is_default: false,
          config: buildDefaultConfig(billingTier || 'free')
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load backtest configurations');
    } finally {
      setLoading(false);
    }
  }, [billingTier, hydrateForm]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleSelectConfig = (cfg) => {
    setActiveId(cfg.id);
    hydrateForm(cfg);
  };

  const handleNewConfig = () => {
    setActiveId(null);
    setFormState({
      name: '',
      description: '',
      is_default: false,
      config: buildDefaultConfig(billingTier || 'free')
    });
  };

  const updateConfigField = (section, field, value) => {
    setFormState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [section]: {
          ...prev.config[section],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (activeConfig?.is_plan_default) {
        setError('Default (plan-dependent) is auto-managed and cannot be edited.');
        return;
      }
      if (!formState.name.trim()) {
        setError('Please provide a name for this configuration.');
        return;
      }
      const payload = {
        name: formState.name.trim(),
        description: formState.description.trim(),
        is_default: formState.is_default,
        config: formState.config
      };
      let nextActiveId = activeId;
      if (activeId) {
        await updateBacktestConfig(activeId, payload);
      } else {
        const created = await createBacktestConfig(payload);
        if (created?.id) {
          nextActiveId = created.id;
        }
      }
      await loadConfigs(nextActiveId);
    } catch (err) {
      setError(err.message || 'Failed to save backtest configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleMakeDefault = async () => {
    if (!activeConfig?.id || activeConfig.is_default) return;
    try {
      setSaving(true);
      setError('');
      await updateBacktestConfig(activeConfig.id, { is_default: true });
      await loadConfigs(activeConfig.id);
    } catch (err) {
      setError(err.message || 'Failed to set default backtest configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!configId) return;
    const targetConfig = configs.find((cfg) => cfg.id === configId);
    if (!targetConfig || targetConfig.is_plan_default) return;
    const confirmationText = targetConfig.is_default
      ? 'Delete this backtest configuration? It is your current default; default will fall back to the plan-dependent one.'
      : 'Delete this backtest configuration?';
    if (!window.confirm(confirmationText)) return;

    try {
      setSaving(true);
      setError('');
      await deleteBacktestConfig(configId);
    } catch (err) {
      setError(err.message || 'Failed to delete backtest configuration');
    } finally {
      setSaving(false);
    }
    const preferredId = configId === activeId ? null : activeId;
    await loadConfigs(preferredId);
  };

  const tier = billingTier || 'free';
  const tierLabel = getTierDisplayName(tier);
  const minStartMonth = getMaxStartDate(new Date(), tier).toISOString().slice(0, 7);

  const HeadingTag = embedded ? 'h2' : 'h1';

  return (
    <div className={`backtest-configs-page${embedded ? ' embedded' : ''}`}>
      <div className={`flex flex-wrap items-center justify-between gap-4 ${embedded ? 'mb-4' : 'mb-8'}`}>
        <div>
          <HeadingTag className={`${embedded ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900`}>
            Backtest Configurations
          </HeadingTag>
          <p className={embedded ? 'text-sm text-gray-500' : 'text-gray-600'}>
            Create reusable settings for weighting, rebalancing, and backtest runs.
          </p>
        </div>
        <Button onClick={handleNewConfig} size={embedded ? 'sm' : 'md'}>
          New Configuration
        </Button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Saved Configurations</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-gray-500">Loading configurations...</div>
            ) : (
              <div className="flex flex-col gap-3">
                {configs.map((cfg) => {
                  const isActive = activeId === cfg.id;
                  return (
                    <div
                      key={cfg.id}
                      className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-left transition ${
                        isActive
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectConfig(cfg)}
                        className={`flex-1 text-left bg-transparent border-0 p-0 ${
                          isActive ? 'text-blue-700' : 'text-gray-900'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium">{cfg.name}</div>
                          <div className="flex items-center gap-1">
                            {cfg.is_plan_default && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-700">
                                Plan
                              </span>
                            )}
                            {cfg.is_default && (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-600">
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                        {cfg.description && (
                          <div className="mt-1 text-xs text-gray-500">{cfg.description}</div>
                        )}
                      </button>
                      {!cfg.is_plan_default && (
                        <button
                          type="button"
                          onClick={() => handleDelete(cfg.id)}
                          className="mt-0.5 rounded-md p-1.5 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Delete configuration"
                          aria-label={`Delete ${cfg.name}`}
                          disabled={saving}
                        >
                          <FiTrash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
                {!loading && configs.length === 0 && (
                  <div className="text-sm text-gray-500">No configurations yet.</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <CardTitle>Configuration Details</CardTitle>
              {activeConfig && !activeConfig.is_default && (
                <Button variant="outline" onClick={handleMakeDefault} disabled={saving}>
                  Make Default
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Your {tierLabel} plan allows backtesting the last {getMaxBacktestYears(tier)} years.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {isPlanDefault && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  This configuration is auto-managed based on your current plan ({tierLabel}).
                  Upgrade/downgrade updates its date range automatically.
                </div>
              )}
              <fieldset disabled={isPlanDefault} className={isPlanDefault ? 'opacity-75' : undefined}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formState.name}
                    onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-md border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <input
                    type="text"
                    value={formState.description}
                    onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full rounded-md border px-3 py-2"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                <h3 className="text-lg font-medium">Weighting</h3>
                <div>
                  <label className="block text-sm font-medium mb-2">Method</label>
                  <select
                    value={formState.config.weighting.method}
                    onChange={(e) => updateConfigField('weighting', 'method', e.target.value)}
                    className="w-full rounded-md border px-3 py-2"
                  >
                    <option value="equal">Equal Weight</option>
                    <option value="cap_weight">Market Cap Weight</option>
                  </select>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                <h3 className="text-lg font-medium">Rebalance</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-2">Frequency</label>
                    <select
                      value={formState.config.rebalance.freq}
                      onChange={(e) => updateConfigField('rebalance', 'freq', e.target.value)}
                      className="w-full rounded-md border px-3 py-2"
                    >
                      <option value="W">Weekly</option>
                      <option value="2W">Bi-Weekly</option>
                      <option value="M">Monthly</option>
                      <option value="Q">Quarterly</option>
                      <option value="6M">Semi-Annual</option>
                      <option value="Y">Annual</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 mt-6">
                    <input
                      id="intra-period-trading"
                      type="checkbox"
                      checked={formState.config.rebalance.intra_period_trading}
                      onChange={(e) => updateConfigField('rebalance', 'intra_period_trading', e.target.checked)}
                    />
                    <label htmlFor="intra-period-trading" className="text-sm">
                      Allow intra-period trading
                    </label>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                <h3 className="text-lg font-medium">Backtest Run</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-2">Start Month</label>
                    <input
                      type="month"
                      value={formState.config.backtest.start_date?.slice(0, 7) || ''}
                      onChange={(e) => {
                        const monthValue = e.target.value;
                        updateConfigField('backtest', 'start_date', monthValue ? `${monthValue}-01` : '');
                      }}
                      min={minStartMonth}
                      max={new Date().toISOString().slice(0, 7)}
                      className="w-full rounded-md border px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">End Date</label>
                    <input
                      type="date"
                      value={formState.config.backtest.end_date || ''}
                      onChange={(e) => updateConfigField('backtest', 'end_date', e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full rounded-md border px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Initial Cash</label>
                    <input
                      type="number"
                      value={formState.config.backtest.initial_cash}
                      onChange={(e) => updateConfigField('backtest', 'initial_cash', Number(e.target.value))}
                      className="w-full rounded-md border px-3 py-2"
                      min="0"
                    />
                  </div>
                  <div>
                  <label className="block text-sm font-medium mb-2">Benchmark</label>
                    <select
                      value={formState.config.backtest.benchmark || ''}
                      onChange={(e) => updateConfigField('backtest', 'benchmark', e.target.value)}
                      className="w-full rounded-md border px-3 py-2"
                    >
                      {BENCHMARK_OPTIONS.map((opt) => (
                        <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                <h3 className="text-lg font-medium">Trading Costs</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-2">Fee (bps)</label>
                    <input
                      type="number"
                      value={formState.config.costs.fee_bps}
                      onChange={(e) => updateConfigField('costs', 'fee_bps', Number(e.target.value))}
                      className="w-full rounded-md border px-3 py-2"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Slippage (bps)</label>
                    <input
                      type="number"
                      value={formState.config.costs.slippage_bps}
                      onChange={(e) => updateConfigField('costs', 'slippage_bps', Number(e.target.value))}
                      className="w-full rounded-md border px-3 py-2"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Dividends</label>
                    <select
                      value={formState.config.costs.dividends}
                      onChange={(e) => updateConfigField('costs', 'dividends', e.target.value)}
                      className="w-full rounded-md border px-3 py-2"
                    >
                      <option value="auto_reinvest">Auto Reinvest</option>
                      <option value="reinvest_at_rebalance">Reinvest at Rebalancing</option>
                      <option value="hold_cash">Hold as Cash</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                <h3 className="text-lg font-medium">Data Handling</h3>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={excludeLargeMoves}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormState((prev) => ({
                          ...prev,
                          config: {
                            ...prev.config,
                            data_handling: {
                              ...(prev.config.data_handling || {}),
                              exclude_large_moves: checked,
                              ...(checked ? { show_large_moves: false } : {})
                            }
                          }
                        }));
                      }}
                    />
                    <span>Remove stocks from the backtest universe if the daily return is more than 100%.</span>
                  </label>
                  {!excludeLargeMoves && (
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={formState.config?.data_handling?.show_large_moves ?? false}
                        onChange={(e) => updateConfigField('data_handling', 'show_large_moves', e.target.checked)}
                      />
                      <span>Show daily returns above 100% in backtest results.</span>
                    </label>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
                {activeConfig && !activeConfig.is_plan_default && (
                  <Button variant="outline" onClick={() => handleDelete(activeId)} disabled={saving}>
                    Delete
                  </Button>
                )}
              </div>
              </fieldset>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BacktestConfigsPage;
